/**
 * useAutoMarkRead — IntersectionObserver-driven auto-mark-as-read for
 * notification cards and conversation threads.
 *
 * A self-contained state machine with no coupling to the rest of
 * `SidePanelNotificationList`, so the parent stays focused on
 * orchestration + JSX and this behaviour carries its own dedicated unit tests.
 *
 * What this hook owns:
 *   - One IntersectionObserver scoped to the scroll container
 *     (`scrollRef.current` — the `.scrollArea` div that scrolls the
 *     recap card + list together), with `threshold: 0.5` per the
 *     dwell-detection design. `scrollRef` is required because the `<ul>`
 *     isn't the scroller: using it as the root would make every card
 *     register as fully intersecting and get marked-read with no dwell.
 *   - Per-card auto-mark-read timers: 1500ms dwell + 300ms batched
 *     flush. A card scrolled into view must remain visible for 1500ms
 *     before its msgId enters the pending set; pending ids are drained
 *     to `onMarkRead` 300ms after the last addition.
 *   - Conversation auto-mark-read mirrors the per-card path with one
 *     difference: it fires `onMarkConversationRead(key)` per conv
 *     (no batching) since conversation read-state typically requires
 *     a server round-trip per thread anyway.
 *   - `markedConvKeys` lifecycle: once a conv is marked read it stays
 *     in the set so the observer doesn't re-fire when it scrolls back
 *     into view; the entry clears when `unreadCount > 0` again so a
 *     read → new-message → unread cycle re-enrolls the card.
 *   - `enrolledConvKeys` lifecycle: cheap dedup so we don't call
 *     `observer.observe()` redundantly on every conversations update
 *     (the API spec says it's a no-op on already-observed targets, but
 *     skipping the call is still cheaper than letting it through).
 *   - A secondary effect that enrolls newly-arrived conversation cards
 *     into the already-running observer without tearing it down — the
 *     primary observer effect intentionally excludes `conversations`
 *     from its dep array so an active 1500ms dwell timer isn't reset
 *     by an unrelated incoming-message render.
 *
 * What this hook DOES NOT own:
 *   - The `<ul>` ref itself — owned by the parent and passed in so the
 *     parent can also query it for keyboard-navigation row lookups.
 *   - Marking-as-read storage writes — surfaced through the
 *     `onMarkRead` / `onMarkConversationRead` callback contract.
 *
 * Ref-mirror note: ref mirrors are written inside `useEffect` (not the
 * render body) so they only run for committed renders — the React 18
 * concurrent-safe idiom. Migrated codebase-wide in #423.
 */

import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";
import { type RefObject, useCallback, useEffect, useRef } from "react";

export interface UseAutoMarkReadOptions {
  /**
   * Ref to the `<ul>` containing the cards. Required as a prop (not
   * created inside the hook) because the parent also uses it for
   * keyboard-navigation row lookups; one shared ref keeps the observed
   * card container and the keyboard's `querySelector` on the same node.
   * (The observer's `root` is the separate `scrollRef`, not this `<ul>`.)
   */
  listRef: RefObject<HTMLUListElement | null>;
  /** IntersectionObserver root (the `.scrollArea` scroller, not the `<ul>`). Must be non-null whenever `disabled` is false (host keeps it in lockstep via `disabled: publicMode`); the effect early-returns while disabled, so the hook never reads it then. */
  scrollRef: RefObject<HTMLElement | null>;
  notifications: DecryptedNotification[];
  conversations: ConversationThread[];
  /**
   * Batched by 300ms — a single call may carry multiple msgIds.
   * Host marks them read in storage.
   */
  onMarkRead: (msgIds: string[]) => void;
  /**
   * Fire-and-forget per-thread. The promise is intentionally not
   * awaited; the hook will never surface or re-throw rejections.
   * The host is solely responsible for logging or retrying on failure
   * (failures are caller-logged); the contract mirrors the
   * `SidePanelNotificationListProps.onMarkConversationRead` JSDoc.
   */
  onMarkConversationRead: (key: string) => Promise<void>;
  /** When true, skip all observation — used to keep auto-mark-read inert in privacy mode. */
  disabled?: boolean;
}

export function useAutoMarkRead({
  listRef,
  scrollRef,
  notifications,
  conversations,
  onMarkRead,
  onMarkConversationRead,
  disabled,
}: UseAutoMarkReadOptions): void {
  // Timer ids use `ReturnType<typeof setTimeout>` rather than `number` to
  // match the rest of `packages/client-ui` (`useReplyState`, `useActionStates`)
  // and stay portable across browser/jsdom (number) and any Node-shimmed
  // environment (NodeJS.Timeout).
  const readTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingReadIds = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks conversation keys that have already been marked read in
  // this session so the observer doesn't fire onMarkConversationRead
  // repeatedly on layout shifts / scroll-back events. Keys are
  // removed when the conversation goes unread again (unreadCount > 0)
  // so that a read → new-message → unread cycle re-enrolls the card.
  const markedConvKeys = useRef<Set<string>>(new Set());
  const convReadTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Hoisted observer ref so the secondary conversations effect can
  // enroll new conv cards into the already-running observer without
  // tearing it down.
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Tracks conv keys the observer is already observing, so the
  // secondary effect doesn't call observer.observe() redundantly on
  // every conversations update. observer.observe() on an already-
  // observed target is a no-op per the IntersectionObserver spec, but
  // avoiding the call is cheaper. Cleared when the primary observer
  // effect tears down; a key is removed when its conversation returns
  // to unread (mirrors the markedConvKeys lifecycle below).
  const enrolledConvKeys = useRef<Set<string>>(new Set());

  // Ref-sync for conversations: the IntersectionObserver effect needs
  // the current conversations list without taking a dependency on it.
  // Without this, every new incoming message would trigger the effect
  // to tear down + rebuild — clearing convReadTimers and resetting
  // any in-progress 1500ms auto-read timers, so a user in an active
  // chat could never reach the threshold.
  //
  // This effect also clears stale entries from markedConvKeys: when
  // a conversation's unreadCount goes back above 0, remove its key
  // so the observer can re-enroll it.
  const conversationsRef = useRef(conversations);
  useEffect(() => {
    conversationsRef.current = conversations;
    for (const conv of conversations) {
      if (conv.unreadCount > 0) {
        markedConvKeys.current.delete(conv.key);
        enrolledConvKeys.current.delete(conv.key);
      }
    }
  }, [conversations]);

  // Mirror onMarkRead into a ref so flushReads doesn't take a
  // changing dep and force the IntersectionObserver effect to
  // re-run on every parent re-render. Same trick the per-card
  // hooks use (`stateRef` in useReplyState). The assignment lives in
  // useEffect (not the render body) so it only runs for committed
  // renders — React 18 concurrent-safe (see #423).
  const onMarkReadRef = useRef(onMarkRead);
  const onMarkConversationReadRef = useRef(onMarkConversationRead);
  useEffect(() => {
    onMarkReadRef.current = onMarkRead;
    onMarkConversationReadRef.current = onMarkConversationRead;
  });

  const flushReads = useCallback(() => {
    if (pendingReadIds.current.size === 0) return;
    const ids = [...pendingReadIds.current];
    pendingReadIds.current.clear();
    onMarkReadRef.current(ids);
    flushTimerRef.current = null;
  }, []);

  useEffect(() => {
    // Privacy mode: skip all observation while disabled. Including `disabled`
    // in the dep array ensures the effect re-runs (and cleans up any lingering
    // observer/timers) when the flag is toggled. Also drain pendingReadIds:
    // an id that finished its 1500ms dwell but whose 300ms batch flush hadn't
    // fired yet would otherwise survive (the cleanup deliberately keeps it) and
    // get ghost-flushed on the next dwell after exiting privacy mode. Entering
    // privacy mode aborts the read gesture, same as the unmount contract — so
    // clear here (NOT in the shared cleanup, which runs on every notifications
    // update and must preserve genuinely-pending reads).
    if (disabled) {
      pendingReadIds.current.clear();
      return;
    }

    // The scroll container (`.scrollArea`) is the observer root, not
    // the `<ul>`. Both refs must be present to set up the observer —
    // see the `scrollRef` JSDoc for why falling back to the `<ul>`
    // would silently mark every card read with no dwell.
    const list = listRef.current;
    const scrollRoot = scrollRef.current;
    // Both refs are non-null whenever disabled=false (see scrollRef JSDoc); this
    // guard is a defensive belt-and-braces. No console.warn — this is a pure-UI
    // package, so the contract lives in the type/docs.
    if (!list || !scrollRoot) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const msgId = el.dataset.msgId;
          const convKey = el.dataset.convKey;

          // ── Regular notification read-marking ──────────────────
          if (msgId) {
            if (entry.isIntersecting) {
              if (!readTimers.current.has(msgId)) {
                const timer = setTimeout(() => {
                  readTimers.current.delete(msgId);
                  pendingReadIds.current.add(msgId);
                  if (flushTimerRef.current != null) clearTimeout(flushTimerRef.current);
                  flushTimerRef.current = setTimeout(flushReads, 300);
                }, 1500);
                readTimers.current.set(msgId, timer);
              }
            } else {
              const timer = readTimers.current.get(msgId);
              if (timer != null) {
                clearTimeout(timer);
                readTimers.current.delete(msgId);
              }
            }
          }

          // ── Conversation read-marking ──────────────────────────
          if (convKey) {
            if (entry.isIntersecting) {
              if (!convReadTimers.current.has(convKey) && !markedConvKeys.current.has(convKey)) {
                const timer = setTimeout(() => {
                  convReadTimers.current.delete(convKey);
                  markedConvKeys.current.add(convKey);
                  // Fire-and-forget per the prop's contract: a `.catch` here
                  // would swallow the rejection from the host's perspective,
                  // and the host is contractually responsible for catching
                  // (see the `onMarkConversationRead` prop JSDoc).
                  void onMarkConversationReadRef.current(convKey);
                }, 1500);
                convReadTimers.current.set(convKey, timer);
              }
            } else {
              const timer = convReadTimers.current.get(convKey);
              if (timer != null) {
                clearTimeout(timer);
                convReadTimers.current.delete(convKey);
              }
            }
          }
        }
      },
      { root: scrollRoot, threshold: 0.5 }
    );
    observerRef.current = observer;

    // Only observe unread regular notifications to avoid unnecessary timers.
    const unreadIds = new Set(notifications.filter((n) => n.isRead === false).map((n) => n.msgId));
    const cards = list.querySelectorAll("[data-msg-id]");
    for (const card of cards) {
      const msgId = (card as HTMLElement).dataset.msgId;
      if (msgId && unreadIds.has(msgId)) {
        observer.observe(card);
      }
    }

    // Only observe unread conversation cards.
    const unreadConvKeys = new Set(
      conversationsRef.current.filter((c) => c.unreadCount > 0).map((c) => c.key)
    );
    const convCards = list.querySelectorAll("[data-conv-key]");
    for (const card of convCards) {
      const convKey = (card as HTMLElement).dataset.convKey;
      if (
        convKey &&
        unreadConvKeys.has(convKey) &&
        !markedConvKeys.current.has(convKey) &&
        !enrolledConvKeys.current.has(convKey)
      ) {
        observer.observe(card);
        enrolledConvKeys.current.add(convKey);
      }
    }

    return () => {
      observer.disconnect();
      observerRef.current = null;
      enrolledConvKeys.current.clear();
      for (const timer of readTimers.current.values()) {
        clearTimeout(timer);
      }
      readTimers.current.clear();
      for (const timer of convReadTimers.current.values()) {
        clearTimeout(timer);
      }
      convReadTimers.current.clear();
      if (flushTimerRef.current != null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      // pendingReadIds is intentionally NOT drained on unmount.
      // Closing the panel is treated as aborting the read gesture:
      // the user dismissed the UI before we confirmed they finished
      // reading. Future implementations could flush here if product
      // requirements change, but for now the contract is "no flush
      // on unmount" (validated by the lifecycle test in
      // useAutoMarkRead.test.tsx: "does not call onMarkRead if
      // unmount happens after dwell but before the 300ms flush").
    };
    // conversations is intentionally excluded — see conversationsRef
    // explanation above. listRef and scrollRef are intentionally
    // excluded — they're caller-supplied refs whose identity is stable
    // (the parent's useRef never changes the object), so including either
    // is a no-op dep. The invariant is that the .scrollArea container is
    // rendered whenever disabled=false (see scrollRef JSDoc); the host's
    // !publicMode branch + `disabled: publicMode` keep them in lockstep.
    // `disabled` is included so toggling privacy mode tears down the observer
    // and cleans up any in-flight timers.
  }, [notifications, flushReads, disabled, listRef.current, scrollRef.current]);

  // Enroll new conversation cards into the running observer whenever
  // the conversations list updates. Without this, a brand-new conv
  // card would never get observer.observe() until an unrelated
  // notifications update re-fired the main effect.
  useEffect(() => {
    const observer = observerRef.current;
    const list = listRef.current;
    if (!observer || !list) return;
    const unreadConvKeys = new Set(
      conversations.filter((c) => c.unreadCount > 0).map((c) => c.key)
    );
    const convCards = list.querySelectorAll("[data-conv-key]");
    for (const card of convCards) {
      const convKey = (card as HTMLElement).dataset.convKey;
      if (
        convKey &&
        unreadConvKeys.has(convKey) &&
        !markedConvKeys.current.has(convKey) &&
        !enrolledConvKeys.current.has(convKey)
      ) {
        observer.observe(card);
        enrolledConvKeys.current.add(convKey);
      }
    }
    // listRef is intentionally excluded — same stable-ref reasoning as
    // the primary observer effect above.
  }, [conversations, listRef.current]);
}
